import React from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";

const InstantSettingsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  config: any;
  onSave: (updatedConfig: any) => void;
}> = ({ open, onClose, config, onSave }) => {
  const [formValues, setFormValues] = React.useState<any>({ ...config });

  React.useEffect(() => {
    if (open) {
      setFormValues({ ...config });
    }
  }, [open, config]);

  const handleChange = (field: string, value: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    if (["walletUsageLimitPercent", "walletUsageLimitPerOrder", "combinedDiscountLimitPercent", "combinedDiscountLimitPerOrder"].some(field => {
      const value = formValues[field];
      return value != null && (!Number.isFinite(value) || value < 0 || (field.endsWith("Percent") && value > 100));
    })) return;
    onSave(formValues);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Instant Order Configuration</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formValues.instantEnabled}
                onChange={(e) => handleChange("instantEnabled", e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>Instant Delivery Enabled</Typography>
                <Typography variant="caption" color="text.secondary">Globally toggle checkout for instant orders</Typography>
              </Box>
            }
          />
          
          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={!!formValues.isAutoAssignEnabled}
                onChange={(e) => handleChange("isAutoAssignEnabled", e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>Rider Auto-Assignment Enabled</Typography>
                <Typography variant="caption" color="text.secondary">Automatically dispatch and route instant orders to available riders</Typography>
              </Box>
            }
          />
          
          <Divider />

          <TextField
            label="Free Delivery Threshold (₹)"
            type="number"
            value={formValues.freeDeliveryThreshold ?? 300}
            onChange={(e) => handleChange("freeDeliveryThreshold", Number(e.target.value))}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Orders above or equal to this amount will get free delivery"
          />

          <TextField
            label="Default Delivery Fee (₹)"
            type="number"
            value={formValues.defaultDeliveryFee ?? 30}
            onChange={(e) => handleChange("defaultDeliveryFee", Number(e.target.value))}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Delivery fee charged for orders below the threshold"
          />

          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>Instant Wallet &amp; Discounts</Typography>
          <FormControlLabel
            control={<Switch checked={formValues.walletEnabled !== false} onChange={e => handleChange("walletEnabled", e.target.checked)} />}
            label="Wallet Enabled for Instant"
          />
          {[
            { field: "walletUsageLimitPercent", label: "Maximum Wallet Usage (%)", fallback: 20 },
            { field: "walletUsageLimitPerOrder", label: "Maximum Wallet Usage per Order (₹)", fallback: 250 },
            { field: "combinedDiscountLimitPercent", label: "Maximum Wallet + Coupon (%)", fallback: 50 },
            { field: "combinedDiscountLimitPerOrder", label: "Maximum Wallet + Coupon per Order (₹)", fallback: 250 },
          ].map(({ field, label, fallback }) => {
            const value = formValues[field] ?? fallback;
            const invalid = !Number.isFinite(value) || value < 0 || (field.endsWith("Percent") && value > 100);
            return <TextField key={field} label={label} type="number" value={Number.isNaN(value) ? "" : value}
              onChange={e => handleChange(field, e.target.value === "" ? NaN : Number(e.target.value))}
              error={invalid} helperText={invalid ? field.endsWith("Percent") ? "Enter a percentage from 0 to 100" : "Enter a non-negative amount" : undefined}
              inputProps={{ min: 0, max: field.endsWith("Percent") ? 100 : undefined, step: "any" }} fullWidth size="small" />;
          })}
          <Divider />
          <TextField
            label="Offline Page Title"
            value={formValues.offlineTitle ?? ""}
            onChange={(e) => handleChange("offlineTitle", e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Heading displayed to users when instant delivery is globally offline (e.g. Launching Soon!)"
            placeholder="e.g. Instant Delivery is Launching Soon!"
          />

          <TextField
            label="Offline Page Message"
            value={formValues.offlineBody ?? ""}
            onChange={(e) => handleChange("offlineBody", e.target.value)}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            helperText="Detailed description displayed to users when instant delivery is globally offline"
            placeholder="e.g. We are putting final touches on our kitchens..."
          />

          <TextField
            label="Tester Whitelisted Phone Numbers"
            value={formValues.testerPhones ?? ""}
            onChange={(e) => handleChange("testerPhones", e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Comma-separated phone numbers to bypass the global offline screen (e.g. +919876543210, +919999999999)"
            placeholder="e.g. +919876543210, +919999999999"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" sx={{ textTransform: "none", fontWeight: 600 }}>
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstantSettingsDialog;
